/**
 * +field: the single character's world economy (docs/design/zones.md Phase 1): character creation,
 * where the hero stands, field kills (client-reported, validated against the map's spawns and a
 * server-clock rate budget), bounties and the levelled starter dungeon's clear reward.
 *
 * Pure: every op returns a new Profile and never mutates its input. Time only comes in as `nowMs`
 * (the server's clock online, Date.now() offline); randomness only from `profileRng`.
 */
import type { ClassId, DungeonDef } from '@keystone/sim';
import { fieldLootLevel, lootGold, lootUnit } from '@keystone/sim';
import { fieldAreaAt, fieldLevelAt } from '@keystone/sim';
import { ZONES, getContentBundle } from '@keystone/content';
import { BASE_ILVL, makeItem } from './items';
import { CLASS_STAT_PROFILES } from './data';
import { rollItem } from './loot';
import { LEVEL_CAP, addXp, killXp, sanitizeHeroName, xpToNext } from './leveling';
import { PROFILE_CLASSES, addCharacterProgress, profileRng, profileStanding, roleOf, wearIfBetter } from './profile';
import {
  type BountyProgress,
  type QuestCompletion,
  type QuestReward,
  acceptBounty,
  bountyArrive,
  bountyDungeon,
  bountyKills,
  bountySettle,
  bountyTalk as talkStep,
  turnInBounty,
} from './bounties';
import { FIELD_KILL_BURST, FIELD_KILLS_PER_SEC, HERO_LOOKS, newHeroCharacter } from './hero';
import { type SideQuestProgress, sideQuestKills } from './sidequestState'; // +quests
import type { HeroCharacter, Item, ItemSlot, KillReportBudget, Profile, Rarity } from './types';
import { ITEM_SLOTS } from './types';
import { gridIdleTotals } from './grid.compile'; // +grid
import { claimRareKeys } from './grid.ops'; // +grid

/** Most kills one report may carry (longer lists are cut). */
export const FIELD_KILLS_MAX_REPORT = 200;
/** A normal dungeon clear is refused this soon after entering (server clock). */
export const DUNGEON_CLEAR_MIN_MS = 120_000;

/** The maps the field ops validate against (default: the content bundle's dungeons). */
export interface FieldWorld {
  dungeons: readonly DungeonDef[];
}
const defaultWorld = (): FieldWorld => getContentBundle();
const mapDef = (w: FieldWorld, id: string): DungeonDef | undefined => w.dungeons.find((d) => d.id === id);

/** Gold one kill drops (small: bounties and the dungeon chest are the real gold). */
export function killGold(level: number, elite = false): number {
  return (1 + Math.floor(Math.max(1, level) / 3)) * (elite ? 5 : 1);
}

/** Levelled-dungeon rewards (the zone data adapter: content-authored numbers go here). */
export interface DungeonReward {
  /** First clear: about one level of XP at the dungeon's level. */
  firstXp: number;
  firstGold: number;
  repeatXp: number;
  repeatGold: number;
  /** Item level of the clear's gear drop. */
  ilvl: number;
  /** Chance of a gear drop on a repeat clear (the first clear always drops one). */
  repeatItemChance: number;
}

/** A levelled dungeon's clear rewards: the zone data's (content ZoneDef.dungeonRewards), else derived from its mob level. */
export function dungeonReward(d: Pick<DungeonDef, 'id' | 'mobLevel'>): DungeonReward {
  const lvl = Math.max(1, d.mobLevel ?? 1);
  const firstXp = xpToNext(lvl);
  const out: DungeonReward = { firstXp, firstGold: 40 + 15 * lvl, repeatXp: Math.round(firstXp / 4), repeatGold: 10 + 4 * lvl, ilvl: BASE_ILVL + 2 * lvl, repeatItemChance: 0.25 };
  const data = Object.values(ZONES).flatMap((z) => z.dungeonRewards).find((r) => r.dungeonId === d.id);
  if (data) Object.assign(out, { firstXp: data.firstClear.xp, firstGold: data.firstClear.gold, repeatXp: data.repeatClear.xp, repeatGold: data.repeatClear.gold });
  return out;
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

/** What every field op reports (the web plays level-ups and toasts from it). */
export interface FieldGain {
  xp: number;
  gold: number;
  /** Levels gained by this op (> 0: play the ding and send the sim `setLevel`). */
  levels: number;
  /** Hero level after the op. */
  level: number;
  /** Chain steps / bounties finished by this op (their rewards are included in xp / gold). */
  completed: QuestCompletion[];
  /** Active bounties that progressed. */
  bounties: BountyProgress[];
  /** The zone's intro chain moved (a step finished or progressed). */
  chainAdvanced: boolean;
  /** +quests: side quests that progressed (kills / collected items). */
  quests?: SideQuestProgress[];
  /** +grid: Grid Key sources this op claimed (rare kills). */
  gridKeys?: string[];
}

export type FieldFail = { ok: false; reason: string };
export type FieldOp<X = object> = ({ ok: true; profile: Profile } & FieldGain & X) | FieldFail;

const fail = (reason: string): FieldFail => ({ ok: false, reason });

/** Working copy + accumulator for one op. */
function start(profile: Profile): { p: Profile; hero: HeroCharacter; gain: FieldGain } | FieldFail {
  if (!profile.character) return fail('No character');
  const p = structuredClone(profile);
  const hero = p.character!;
  if (!p.characters[hero.classId]) addCharacterProgress(p, hero.classId);
  return { p, hero, gain: { xp: 0, gold: 0, levels: 0, level: p.characters[hero.classId]!.hero.level, completed: [], bounties: [], chainAdvanced: false } };
}

function grant(p: Profile, hero: HeroCharacter, gain: FieldGain, xp: number, gold: number): void {
  const ch = p.characters[hero.classId]!;
  const xpIn = ch.hero.level < LEVEL_CAP ? Math.max(0, Math.round(xp)) : 0;
  gain.levels += addXp(ch, xpIn);
  gain.xp += xpIn;
  gain.level = ch.hero.level;
  const g = Math.max(0, Math.round(gold));
  p.currencies.gold = (p.currencies.gold ?? 0) + g;
  gain.gold += g;
}

function payQuests(p: Profile, hero: HeroCharacter, gain: FieldGain, done: readonly QuestCompletion[]): void {
  for (const c of done) {
    gain.completed.push(c);
    if (c.kind === 'chain') gain.chainAdvanced = true;
    grant(p, hero, gain, c.reward.xp, c.reward.gold);
  }
  // Rewards can level the hero: a 'level' step that is now met completes at once (and so on).
  const more = bountySettle(hero, hero.zoneId, p.characters[hero.classId]!.hero.level);
  if (more.length) payQuests(p, hero, gain, more);
}

/** End of an op: settle steps already met (a 'level' reached by XP, a 'goto' for where the hero stands). */
function settle(p: Profile, hero: HeroCharacter, gain: FieldGain): void {
  payQuests(p, hero, gain, []);
}

/** +prof: the op helpers, for professions.ts (gathering pays XP / gold and settles quest steps the same way). */
export { start as fieldOpStart, grant as fieldOpGrant, settle as fieldOpSettle };

// ---------------------------------------------------------------------------
// Character creation
// ---------------------------------------------------------------------------

/** Create the profile's one character (starter gear, level 1, in Mossvale's town). Fails if one exists. */
export function createCharacter(profile: Profile, classId: ClassId, rawName: unknown, look = 0): { ok: true; profile: Profile; name: string } | FieldFail {
  if (profile.character) return fail('You already have a character');
  if (!PROFILE_CLASSES.includes(classId)) return fail('Unknown class');
  const name = sanitizeHeroName(rawName);
  if (!name) return fail('Names are 2–16 letters (single spaces, apostrophes or hyphens between them)');
  const lk = Math.max(0, Math.min(HERO_LOOKS - 1, Math.floor(look) || 0));
  const p = structuredClone(profile);
  if (!p.characters[classId]) addCharacterProgress(p, classId);
  for (const [cid, ch] of Object.entries(p.characters)) ch.autoManaged = cid !== classId;
  p.mainClassId = classId;
  p.character = newHeroCharacter(classId, name, lk);
  return { ok: true, profile: p, name };
}

// ---------------------------------------------------------------------------
// Moving around
// ---------------------------------------------------------------------------

/** Save the hero's map and position (a field / town map); completes a 'goto' chain step. */
export function fieldMove(profile: Profile, mapId: string, pos: { x: number; y: number } | null = null, facing = 0, world: FieldWorld = defaultWorld()): FieldOp {
  const map = mapDef(world, mapId);
  if (!map?.field) return fail('Unknown map');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  hero.zoneId = map.field.zoneId;
  hero.mapId = mapId;
  hero.pos = pos && Number.isFinite(pos.x) && Number.isFinite(pos.y) && Math.abs(pos.x) < 1e5 && Math.abs(pos.y) < 1e5 ? { x: pos.x, y: pos.y } : null;
  hero.facing = Number.isFinite(facing) ? facing : 0;
  payQuests(p, hero, gain, bountyArrive(hero, hero.zoneId, p.characters[hero.classId]!.hero.level));
  return { ok: true, profile: p, ...gain };
}

// ---------------------------------------------------------------------------
// +zone: area discovery
// ---------------------------------------------------------------------------

/**
 * First visit to a named area of an open map (docs/design/zones.md "Discovery"): pays
 * `discoverKills` kill-units of XP at the area's level (with the usual level-gap penalty) once per
 * character, and saves the position. `pos` must lie in the area (sub-areas count for their region
 * only when asked by the region's own id).
 */
export function discoverArea(profile: Profile, mapId: string, areaId: string, pos: { x: number; y: number }, world: FieldWorld = defaultWorld()): FieldOp<{ discovered: boolean; areaName: string; bonus: number }> {
  const map = mapDef(world, mapId);
  const area = map?.field?.areas?.find((a) => a.id === areaId);
  if (!map?.field || !area) return fail('Unknown area');
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return fail('No position');
  const here = fieldAreaAt(map.field, pos);
  if (!here || (here.id !== areaId && here.region !== areaId)) return fail('Not in that area');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  if (hero.mapId !== mapId) return fail('Not on that map');
  hero.pos = { x: pos.x, y: pos.y };
  const key = `${mapId}:${areaId}`;
  const seen = (hero.discovered ??= []);
  if (seen.includes(key)) {
    settle(p, hero, gain);
    return { ok: true, profile: p, ...gain, discovered: false, areaName: area.name, bonus: 0 };
  }
  seen.push(key);
  const band = area.levelBand ?? map.field.levelBand;
  const lvl = Math.round((band[0] + band[1]) / 2);
  const ch = p.characters[hero.classId]!;
  const bonus = Math.round((area.discoverKills ?? 0) * killXp(lvl, ch.hero.level));
  if (bonus > 0) grant(p, hero, gain, bonus, 0);
  // Reaching an area can finish a 'goto' step.
  payQuests(p, hero, gain, bountyArrive(hero, hero.zoneId, ch.hero.level));
  return { ok: true, profile: p, ...gain, discovered: true, areaName: area.name, bonus: gain.xp };
}

/** +zone: how close a hero must come to a waystone to attune it. */
export const WAYSTONE_ATTUNE_YD = 12;

/**
 * +zone: attune a waystone (the hero walked up to it). Idempotent; the attuned list is what the
 * waystone panel offers as destinations. Saves the position.
 */
export function attuneWaystone(profile: Profile, mapId: string, waystoneId: string, pos: { x: number; y: number }, world: FieldWorld = defaultWorld()): FieldOp<{ attuned: boolean; name: string }> {
  const map = mapDef(world, mapId);
  const ws = map?.field?.waystones?.find((w) => w.id === waystoneId);
  if (!map?.field || !ws) return fail('Unknown waystone');
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return fail('No position');
  if (Math.hypot(pos.x - ws.pos.x, pos.y - ws.pos.y) > WAYSTONE_ATTUNE_YD) return fail('Too far from the waystone');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  if (hero.mapId !== mapId) return fail('Not on that map');
  hero.pos = { x: pos.x, y: pos.y };
  const key = `${mapId}:${waystoneId}`;
  const list = (hero.waystones ??= []);
  const fresh = !list.includes(key);
  if (fresh) list.push(key);
  settle(p, hero, gain);
  return { ok: true, profile: p, ...gain, attuned: fresh, name: ws.name };
}

// ---------------------------------------------------------------------------
// Field kills
// ---------------------------------------------------------------------------

export interface ReportedKill {
  npcId: string;
  level: number;
  elite?: boolean;
}

/** Where a reported kill may come from on `map`: its level range and whether it is an elite. Null = not on this map. */
export function killSource(map: DungeonDef, npcId: string): { lo: number; hi: number; elite: boolean; anyElite: boolean } | null {
  if (map.field) {
    const spawns = map.field.spawns.filter((s) => s.npcIds.includes(npcId));
    if (!spawns.length) return null;
    return {
      lo: Math.min(...spawns.map((s) => s.level[0])),
      hi: Math.max(...spawns.map((s) => s.level[1])),
      elite: spawns.every((s) => !!s.elite),
      anyElite: spawns.some((s) => !!s.elite),
    };
  }
  if (map.mobLevel !== undefined) {
    const packs = map.packs.filter((pk) => pk.spawns.some((sp) => sp.npcId === npcId));
    if (!packs.length) return null;
    const boss = packs.every((pk) => !!pk.encounterId);
    return { lo: map.mobLevel, hi: map.mobLevel, elite: boss, anyElite: packs.some((pk) => !!pk.encounterId) };
  }
  return null;
}

/** Refill a kill-report budget to `nowMs` (time running backwards refills nothing). */
export function refillBudget(b: KillReportBudget, nowMs: number): KillReportBudget {
  if (b.lastMs === null) return { lastMs: nowMs, budget: Math.min(FIELD_KILL_BURST, Math.max(0, b.budget)) };
  const dt = Math.max(0, nowMs - b.lastMs);
  return { lastMs: Math.max(b.lastMs, nowMs), budget: Math.min(FIELD_KILL_BURST, Math.max(0, b.budget) + (dt / 1000) * FIELD_KILLS_PER_SEC) };
}

/**
 * Fold a batch of client-reported kills on `mapId` into the profile. Each kill must be an NPC that
 * spawns there (field spawns, or a levelled dungeon's packs); its level is clamped to that spawn's
 * range and its elite flag comes from the map. At most the budget's whole kills are accepted (the
 * rest are dropped silently). Pays killXp + killGold per kill and advances the chain / bounties.
 */
export function applyFieldKills(profile: Profile, mapId: string, kills: readonly ReportedKill[], nowMs: number, world: FieldWorld = defaultWorld(), auto = false): FieldOp<{ accepted: number; dropped: number }> {
  if (!Number.isFinite(nowMs)) return fail('no clock');
  const map = mapDef(world, mapId);
  if (!map || (!map.field && map.mobLevel === undefined)) return fail('Unknown map');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const valid: { npcId: string; level: number; elite: boolean }[] = [];
  for (const k of kills.slice(0, FIELD_KILLS_MAX_REPORT)) {
    const src = typeof k?.npcId === 'string' ? killSource(map, k.npcId) : null;
    if (!src) continue;
    const lvl = Number.isFinite(k.level) ? Math.round(k.level) : src.lo;
    valid.push({ npcId: k.npcId, level: Math.max(src.lo, Math.min(src.hi, lvl)), elite: src.elite || (src.anyElite && k.elite === true) });
  }
  const budget = refillBudget(hero.report, nowMs);
  const n = Math.min(valid.length, Math.floor(budget.budget));
  hero.report = { lastMs: budget.lastMs, budget: budget.budget - n };
  const accepted = valid.slice(0, n);
  const ch = p.characters[hero.classId]!;
  const goldMult = 1 + (gridIdleTotals(ch.grid?.nodes ?? []).goldFind ?? 0); // +grid: Coin Sense stars
  for (const k of accepted) grant(p, hero, gain, killXp(k.level, ch.hero.level, k.elite), killGold(k.level, k.elite) * goldMult);
  const keys = claimRareKeys(ch, accepted.map((k) => k.npcId)); // +grid: rare named mobs carry a Grid Key (once each)
  if (keys.length) gain.gridKeys = keys;
  const firstKill = hero.kills; // +quests: collect drops roll on the kill counter
  hero.kills += accepted.length;
  const chainBefore = hero.bounties.chainProgress[hero.zoneId] ?? 0;
  const stepBefore = hero.bounties.chain[hero.zoneId] ?? 0;
  // Auto earns full XP (docs/design/zones.md "Auto"); `auto` only advances the Auto tutorial step.
  const q = bountyKills(hero, hero.zoneId, accepted.map((k) => ({ npcId: k.npcId, mapId })), auto);
  payQuests(p, hero, gain, q.completed);
  settle(p, hero, gain);
  gain.bounties = q.bounties;
  const sq = sideQuestKills(hero, accepted, firstKill); // +quests
  if (sq.length) gain.quests = sq;
  if ((hero.bounties.chainProgress[hero.zoneId] ?? 0) !== chainBefore || (hero.bounties.chain[hero.zoneId] ?? 0) !== stepBefore) gain.chainAdvanced = true;
  return { ok: true, profile: p, ...gain, accepted: accepted.length, dropped: kills.length - accepted.length };
}

// ---------------------------------------------------------------------------
// Bounties
// ---------------------------------------------------------------------------

/** Talk to a town NPC: completes the chain's 'talk' step for it. */
export function talkTo(profile: Profile, npcId: string): FieldOp {
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  // The NPC's own map (town or field): the hero must stand on it.
  const npcMap = defaultWorld().dungeons.find((d) => d.field?.npcs?.some((n) => n.id === npcId))?.id;
  const done = talkStep(hero, hero.zoneId, npcId, npcMap);
  if (!done) return fail('Nothing to talk about');
  payQuests(p, hero, gain, done);
  return { ok: true, profile: p, ...gain };
}

export function bountyAccept(profile: Profile, bountyId: string): FieldOp {
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const err = acceptBounty(hero, bountyId, p.characters[hero.classId]!.hero.level);
  if (err) return fail(err);
  gain.bounties = [{ id: bountyId, progress: 0, count: 0 }];
  return { ok: true, profile: p, ...gain };
}

export function bountyTurnIn(profile: Profile, bountyId: string): FieldOp {
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const r = turnInBounty(hero, bountyId);
  if (typeof r === 'string') return fail(r);
  payQuests(p, hero, gain, [r]);
  return { ok: true, profile: p, ...gain };
}

// ---------------------------------------------------------------------------
// The levelled starter dungeon (normal difficulty)
// ---------------------------------------------------------------------------

/** Record entering a levelled dungeon (starts the clear-time floor). */
export function dungeonEnter(profile: Profile, dungeonId: string, nowMs: number, world: FieldWorld = defaultWorld()): FieldOp {
  if (!Number.isFinite(nowMs)) return fail('no clock');
  const d = mapDef(world, dungeonId);
  if (!d || d.field || d.mobLevel === undefined) return fail('Unknown dungeon');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  hero.dungeonEnteredMs = nowMs;
  hero.dungeonEnteredId = dungeonId;
  payQuests(p, hero, gain, bountyDungeon(hero, hero.zoneId, dungeonId, 'enter'));
  return { ok: true, profile: p, ...gain };
}

const DUNGEON_SALT = 0x6c1e_a4b1;

/**
 * Pay a levelled dungeon clear: the first clear gives about a level of XP, a guaranteed gear item
 * for the hero's class and chest gold; repeats pay less (and sometimes an item). Refused unless the
 * dungeon was entered at least DUNGEON_CLEAR_MIN_MS ago (server clock). Rolls (profile seed).
 */
export function dungeonClear(profile: Profile, dungeonId: string, nowMs: number, world: FieldWorld = defaultWorld()): FieldOp<{ first: boolean; item: Item | null; equipped: boolean; reward: QuestReward }> {
  if (!Number.isFinite(nowMs)) return fail('no clock');
  const d = mapDef(world, dungeonId);
  if (!d || d.field || d.mobLevel === undefined) return fail('Unknown dungeon');
  const hero0 = profile.character;
  if (!hero0) return fail('No character');
  if (hero0.dungeonEnteredMs === null || (hero0.dungeonEnteredId ?? null) !== dungeonId) return fail('Not in that dungeon');
  if (nowMs - hero0.dungeonEnteredMs < DUNGEON_CLEAR_MIN_MS) return fail('Too fast');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const clears = hero.dungeonClears[dungeonId] ?? 0;
  const first = clears === 0;
  const rw = dungeonReward(d);
  const reward = first ? { xp: rw.firstXp, gold: rw.firstGold } : { xp: rw.repeatXp, gold: rw.repeatGold };
  hero.dungeonClears[dungeonId] = clears + 1;
  hero.dungeonEnteredMs = null;
  hero.dungeonEnteredId = null;
  grant(p, hero, gain, reward.xp, reward.gold);
  payQuests(p, hero, gain, bountyDungeon(hero, hero.zoneId, dungeonId, 'clear'));
  // Gear: rolled on the profile seed, salted by the clear count and id counter (server-side seed online).
  const rng = profileRng(p, (DUNGEON_SALT ^ Math.imul(clears + 1, 0x9e3779b1) ^ p.nextId) >>> 0);
  let item: Item | null = null;
  let out = p;
  if (first || rng.chance(rw.repeatItemChance)) {
    item = rollItem({ classId: hero.classId, role: roleOf(hero.classId), ilvl: rw.ilvl, tier: profileStanding(p).tier, nextId: () => `i${p.nextId++}`, source: 'run' }, rng);
    p.inventory.push(item);
    out = wearIfBetter(p, hero.classId, item.id);
  }
  return { ok: true, profile: out, ...gain, first, item, equipped: out !== p, reward };
}

// ---------------------------------------------------------------------------
// Ground pickups (XP wisps, gold piles / pouches / chests, gear drops)
// ---------------------------------------------------------------------------

/** A pickup the client's sim says the hero collected (`fieldPickups`). */
export interface ReportedPickup {
  kind: 'gold' | 'xp' | 'item';
  amount: number;
  /** Gear drops: the rarity the sim rolled (capped at PICKUP_MAX_RARITY; the server rolls the item). */
  rarity?: string;
}

/**
 * Server-clock budget for pickups, per minute, in units of the map's loot level: XP in even-level
 * kills (`lootUnit`), gold in coin pouches (`lootGold.pouch`), items in drops. Expected play takes
 * about 3 kill-units of XP and 1.5 pouches of gold a minute (zones.md "Pickups"), so these only
 * stop a client that invents pickups; bursts cover a chest or the sprite at once.
 */
export const PICKUP_RATES = {
  xpPerMin: 10,
  xpBurst: 25,
  goldPerMin: 6,
  goldBurst: 16,
  itemsPerMin: 1,
  itemsBurst: 3,
} as const;
/** Pickups never roll above this rarity (legendaries come from the forge and dungeons). */
export const PICKUP_MAX_RARITY: Rarity = 'epic';
/** Most pickups one report may carry. */
export const FIELD_PICKUPS_MAX_REPORT = 100;

const RANK: readonly Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const capRarity = (r: unknown): Rarity => {
  const i = RANK.indexOf(typeof r === 'string' ? (r as Rarity) : 'common');
  return RANK[Math.max(0, Math.min(i, RANK.indexOf(PICKUP_MAX_RARITY)))] ?? 'common';
};

/** Refill the pickup budget to `nowMs` for loot level `lvl` (time running backwards refills nothing). */
export function refillPickups(b: HeroCharacter['pickupBudget'], nowMs: number, lvl: number): NonNullable<HeroCharacter['pickupBudget']> {
  const xpU = lootUnit(lvl);
  const gU = lootGold.pouch(lvl);
  const cap = { xp: PICKUP_RATES.xpBurst * xpU, gold: PICKUP_RATES.goldBurst * gU, items: PICKUP_RATES.itemsBurst };
  if (!b || b.lastMs === null) return { lastMs: nowMs, ...cap };
  const min = Math.max(0, nowMs - b.lastMs) / 60_000;
  return {
    lastMs: Math.max(b.lastMs, nowMs),
    xp: Math.min(cap.xp, Math.max(0, b.xp) + min * PICKUP_RATES.xpPerMin * xpU),
    gold: Math.min(cap.gold, Math.max(0, b.gold) + min * PICKUP_RATES.goldPerMin * gU),
    items: Math.min(cap.items, Math.max(0, b.items) + min * PICKUP_RATES.itemsPerMin),
  };
}

const PICKUP_SALT = 0x51ce_b00c;

/**
 * Fold a batch of ground pickups on `mapId` (a field with loot) into the profile: XP and gold are
 * paid up to the server-clock budget (the rest is dropped), each gear drop rolls a real item for the
 * hero's class (rarity capped, profile seed) and is worn if better. Rolls.
 */
export function applyFieldPickups(profile: Profile, mapId: string, pickups: readonly ReportedPickup[], nowMs: number, world: FieldWorld = defaultWorld()): FieldOp<{ items: Item[]; equipped: boolean; dropped: number }> {
  if (!Number.isFinite(nowMs)) return fail('no clock');
  const map = mapDef(world, mapId);
  if (!map?.field?.loot) return fail('No loot here');
  const s = start(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  // +zone: on a map with areas, loot is sized for the area the hero last stood in (the sim does the same).
  const band = map.field.areas?.length && hero.pos && hero.mapId === mapId ? fieldLevelAt(map.field, hero.pos) : null;
  const lvl = band ? Math.round((band[0] + band[1]) / 2) : fieldLootLevel(map.field);
  const b = refillPickups(hero.pickupBudget, nowMs, lvl);
  let xp = 0;
  let gold = 0;
  let dropped = 0;
  const wanted: Rarity[] = [];
  for (const q of pickups.slice(0, FIELD_PICKUPS_MAX_REPORT)) {
    const amt = Number.isFinite(q?.amount) ? Math.max(0, Math.round(q.amount)) : 0;
    if (q?.kind === 'xp') {
      const take = Math.min(amt, Math.floor(b.xp));
      b.xp -= take;
      xp += take;
      if (take < amt) dropped++;
    } else if (q?.kind === 'gold') {
      const take = Math.min(amt, Math.floor(b.gold));
      b.gold -= take;
      gold += take;
      if (take < amt) dropped++;
    } else if (q?.kind === 'item') {
      if (b.items >= 1) {
        b.items -= 1;
        wanted.push(capRarity(q.rarity));
      } else dropped++;
    } else dropped++;
  }
  dropped += Math.max(0, pickups.length - FIELD_PICKUPS_MAX_REPORT);
  hero.pickupBudget = b;
  grant(p, hero, gain, xp, gold);
  settle(p, hero, gain);
  const items: Item[] = [];
  let out = p;
  if (wanted.length) {
    const rng = profileRng(p, (PICKUP_SALT ^ Math.imul(p.nextId, 0x9e3779b1)) >>> 0);
    const ilvl = BASE_ILVL + 2 * lvl;
    const priority = CLASS_STAT_PROFILES[hero.classId]?.priority;
    for (const rarity of wanted) {
      const slot = rng.pick(ITEM_SLOTS) as ItemSlot;
      const it = makeItem({ id: `i${p.nextId++}`, slot, ilvl, rarity, source: 'run', ...(priority ? { priority } : {}) }, rng);
      p.inventory.push(it);
      items.push(it);
    }
    for (const it of items) out = wearIfBetter(out, hero.classId, it.id);
  }
  return { ok: true, profile: out, ...gain, items, equipped: out !== p, dropped };
}
